/**
 * PortElementsList — listado compartido de **PDUs**, **Patch Panels** y **Switches**.
 *
 * Los tres tipos son la misma familia («elementos con puertos»), así que usan el
 * mismo componente con `elementType` y el andamiaje del estándar
 * (`common/ListPage.jsx`): buscador, filtros, exportar a Excel, columnas, cebra y
 * clic en la fila para abrir la ficha EN LECTURA.
 *
 * Lo propio de cada tipo está en `CONFIG`:
 *  · **PDUs** — *Mapa de enchufes* (`PDUOutletMap`), *Ver rack* y *Ver cableado de
 *    alimentación* (Canvas Alimentación). Filtro extra de **ocupación de
 *    enchufes** (el backend la calcula para toda la lista en 3 consultas).
 *  · **Patch Panels** — *Mapa de puertos*, *Ver cableado* (Canvas Cableado),
 *    *Ver rack* y *Componentes*.
 *  · **Switches** — igual que los patch panels, con los datos PoE en la tabla.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapIcon,
  CubeIcon,
  CpuChipIcon,
  BoltIcon,
  ShareIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowsRightLeftIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import Dialog from './common/Dialog';
import CloneDialog from './common/CloneDialog';
import ComponentsModal from './ComponentsModal';
import PortElementForm from './PortElementForm';
import PortMapModal from './PortMapModal';
import PDUOutletMap from './PDUOutletMap';
import { getServerFormStyle } from '../utils/serverFormStyle';
import useNavOrder from '../hooks/useNavOrder';
import { useListView } from '../views/ViewsContext';
import './PortElementsList.css';

const CONFIG = {
  panel: {
    icon: '🔗',
    heroIcon: TableCellsIcon,
    i18n: 'panel',
    title: 'Patch Panels',
    singular: 'Patch Panel',
    emptyMsg: 'No hay patch panels registrados.',
    viewPorts: 'Mapa de puertos',
    wiringPath: '/infrastructure/network-canvas',
    wiringLabel: 'Ver cableado',
    wiringIcon: 'share',
    hasComponents: true,
    hasOccupancyFilter: false,
    exportBase: 'patch-panels',
  },
  switch: {
    icon: '🔌',
    heroIcon: ArrowsRightLeftIcon,
    i18n: 'switch',
    title: 'Switches de red',
    singular: 'Switch',
    emptyMsg: 'No hay switches registrados.',
    viewPorts: 'Mapa de puertos',
    wiringPath: '/infrastructure/network-canvas',
    wiringLabel: 'Ver cableado',
    wiringIcon: 'share',
    hasComponents: true,
    hasOccupancyFilter: false,
    exportBase: 'switches',
  },
  pdu: {
    icon: '⚡',
    heroIcon: BoltIcon,
    i18n: 'pdu',
    title: 'PDUs',
    singular: 'PDU',
    emptyMsg: 'No hay PDUs registradas.',
    viewPorts: 'Mapa de enchufes',
    wiringPath: '/infrastructure/power-canvas',
    wiringLabel: 'Ver cableado de alimentación',
    wiringIcon: 'bolt',
    hasComponents: false,
    hasOccupancyFilter: true,
    exportBase: 'pdus',
  },
};

const STATUS_META = {
  active:      { label: 'Activo',            badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento',     badge: 'list-badge--warn' },
  offline:     { label: 'Fuera de servicio', badge: 'list-badge--off' },
};

function PortElementsList({ elementType, companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cfg = CONFIG[elementType] || CONFIG.panel;
  // Preferencia global: ficha clásica o variante tarjetas (estándar del CMDB).
  const estiloFicha = getServerFormStyle();
  // Claves i18n por tipo: `pdu.name`, `panel.name`, `switch.name`…
  const trc = useCallback((suffix, fallback) => tr(`${cfg.i18n}.${suffix}`, fallback), [tr, cfg.i18n]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterRack, setFilterRack] = useState('');
  const [filterController, setFilterController] = useState('');
  const [filterOccupancy, setFilterOccupancy] = useState('');
  const [controllers, setControllers] = useState([]);
  const [hlId, setHlId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [editItem, setEditItem] = useState(null);

  const [itemToCopy, setItemToCopy] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [componentsItem, setComponentsItem] = useState(null);

  const [portModalItem, setPortModalItem] = useState(null);
  const [portModalRackElements, setPortModalRackElements] = useState([]);

  useEffect(() => {
    if (companyId) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, elementType]);

  // Los switches se pueden filtrar por el controlador que los gestiona, igual
  // que los APs WiFi. Paneles y PDUs no tienen controlador.
  useEffect(() => {
    if (!companyId || elementType !== 'switch') {
      setControllers([]);
      return undefined;
    }
    let cancelled = false;
    api.getWifiControllers(companyId)
      .then((res) => { if (!cancelled) setControllers(res.data || []); })
      .catch(() => { if (!cancelled) setControllers([]); });
    return () => { cancelled = true; };
  }, [companyId, elementType]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getElements(companyId, { element_type: elementType });
      setItems(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortModal = (item) => {
    // El propio modal carga todos los elementos de la empresa y filtra por
    // la raíz de la ubicación: así se pueden unir equipos de plantas distintas.
    setPortModalRackElements([]);
    setPortModalItem(item);
  };

  /* ── Ficha (lectura por defecto) ───────────────────────────────────── */
  const handleOpen = (item) => { setEditItem(item); setFormMode('view'); setShowForm(true); };
  const handleEdit = (item) => { setEditItem(item); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setEditItem(null); setFormMode('edit'); setShowForm(true); };

  const handleDelete = async (item) => {
    try {
      await api.deleteElement(item.id, companyId);
      await fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  /* ── Copiar (nombre en un diálogo, no en un prompt) ────────────────── */
  const handleCopy = async (name) => {
    setCopyBusy(true);
    setDialogError('');
    try {
      await api.cloneElement(itemToCopy.id, name, companyId);
      setItemToCopy(null);
      await fetchItems();
    } catch (err) {
      setDialogError(err.message || 'No se pudo copiar el elemento');
    } finally {
      setCopyBusy(false);
    }
  };

  /* ── Resaltado con ?destacar=<id>[&abrir=1] ─────────────────────────── */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find((i) => i.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { handleOpen(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`port-element-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items, setSearchParams]);

  /* ── Ocupación (puertos o enchufes) ────────────────────────────────── */
  const summaryOf = (item) => item.ports_summary || { total: item.port_count || 0, connected: 0 };
  const occupancyPct = (item) => {
    const s = summaryOf(item);
    return s.total > 0 ? Math.round((s.connected / s.total) * 100) : 0;
  };

  /* ── Filtros ───────────────────────────────────────────────────────── */
  const unique = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  const locationOptions = useMemo(() => unique(items.map((i) => i.location_name)), [items]);
  const rackOptions = useMemo(() => unique(items.map((i) => i.rack_name)), [items]);

  const filters = [
    {
      key: 'status',
      label: tr('list.filter_status', 'Estado'),
      allLabel: tr('list.filter_all_states', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: [
        { value: 'active', label: STATUS_META.active.label },
        { value: 'maintenance', label: STATUS_META.maintenance.label },
        { value: 'offline', label: STATUS_META.offline.label },
      ],
    },
    {
      key: 'location',
      label: tr('list.filter_location', 'Ubicación'),
      allLabel: tr('list.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLocation,
      onChange: setFilterLocation,
      options: locationOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'rack',
      label: tr('list.filter_rack', 'Rack'),
      allLabel: tr('list.filter_all_racks', 'Todos los racks'),
      value: filterRack,
      onChange: setFilterRack,
      options: rackOptions.map((v) => ({ value: v, label: v })),
    },
    ...(cfg.hasOccupancyFilter ? [{
      key: 'occupancy',
      label: tr('list.filter_occupancy', 'Ocupación'),
      allLabel: tr('list.filter_all_occupancy', 'Cualquier ocupación'),
      value: filterOccupancy,
      onChange: setFilterOccupancy,
      options: [
        { value: 'free', label: 'Con enchufe libre' },
        { value: 'full', label: 'Llenas' },
      ],
    }] : []),
    ...(cfg.i18n === 'switch' ? [{
      key: 'controller',
      label: tr('list.filter_controller', 'Controlador'),
      allLabel: tr('list.filter_all_controllers', 'Todos los controladores'),
      value: filterController,
      onChange: setFilterController,
      options: [
        ...controllers.map((c) => ({
          value: String(c.id),
          label: c.sites_label ? `${c.name} · ${c.sites_label}` : c.name,
        })),
        { value: 'none', label: tr('list.no_controller', 'Sin controlador') },
      ],
    }] : []),
  ];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch = !q
        || (item.name || '').toLowerCase().includes(q)
        || (item.code || '').toLowerCase().includes(q)
        || (item.rack_name || '').toLowerCase().includes(q)
        || (item.location_name || '').toLowerCase().includes(q)
        || (item.manufacturer || '').toLowerCase().includes(q)
        || (item.model || '').toLowerCase().includes(q)
        || (item.serial_number || '').toLowerCase().includes(q)
        || (item.ip_address || '').toLowerCase().includes(q)
        || (item.controller_name || '').toLowerCase().includes(q);
      const matchesStatus = !filterStatus || item.status === filterStatus;
      const matchesLocation = !filterLocation || item.location_name === filterLocation;
      const matchesRack = !filterRack || item.rack_name === filterRack;
      const matchesController = !filterController
        || (filterController === 'none'
          ? !item.controller_id
          : String(item.controller_id) === filterController);
      const s = summaryOf(item);
      const matchesOccupancy = !filterOccupancy
        || (filterOccupancy === 'full' ? s.total > 0 && s.connected >= s.total
          : s.total === 0 || s.connected < s.total);
      return matchesSearch && matchesStatus && matchesLocation && matchesRack
        && matchesController && matchesOccupancy;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, filterStatus, filterLocation, filterRack, filterController, filterOccupancy]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navItems = useNavOrder(filtered, editItem, showForm);

  const totalPorts = filtered.reduce((sum, i) => sum + (summaryOf(i).total || 0), 0);
  const totalConnected = filtered.reduce((sum, i) => sum + (summaryOf(i).connected || 0), 0);

  /* ── Columnas (catálogo completo; 7 visibles de entrada) ───────────────
   * El catálogo entero va al Excel; en pantalla se ven las esenciales y el
   * resto se activa desde el selector de columnas.
   */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: trc('name', 'Nombre'),
        sortable: true,
        sortValue: (item) => item.name || '',
        render: (item) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {item.name}
            {item.poe_capable && (
              <span
                className="list-badge list-badge--warn"
                title={item.poe_budget_w ? `PoE · ${item.poe_budget_w} W` : 'Con PoE'}
              >
                ⚡ PoE
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'code',
        label: tr('list.code', 'Código'),
        sortable: true,
        render: (item) => (item.code ? <span className="list-code">{item.code}</span> : dash),
      },
      {
        key: 'ports',
        label: trc('ports', 'Puertos'),
        sortable: true,
        sortValue: (item) => summaryOf(item).total || 0,
        render: (item) => summaryOf(item).total || dash,
      },
      {
        key: 'occupancy',
        label: trc('occupancy', 'Ocupación'),
        sortable: true,
        sortValue: (item) => occupancyPct(item),
        render: (item) => {
          const s = summaryOf(item);
          if (!s.total) return dash;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="pel-port-bar" style={{ width: 60 }}>
                <span className="pel-port-bar-fill" style={{ width: `${occupancyPct(item)}%` }} />
              </span>
              <span className="list-hint">{s.connected}/{s.total}</span>
            </span>
          );
        },
      },
      {
        key: 'location_name',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (item) => item.location_name || '',
        render: (item) => item.location_name || dash,
      },
      {
        key: 'rack_name',
        label: tr('list.rack_position', 'Rack / Posición'),
        sortable: true,
        sortValue: (item) => item.rack_name || '',
        render: (item) => (item.rack_name
          ? <span>{item.rack_name}{item.position_u ? <span className="list-hint"> · U{item.position_u}</span> : null}</span>
          : <span className="list-hint">Sin rack</span>),
      },
      ...(cfg.i18n === 'switch' ? [{
        key: 'controller_name',
        label: tr('list.controller', 'Controlador'),
        sortable: true,
        sortValue: (item) => item.controller_name || '',
        render: (item) => item.controller_name || dash,
      }] : []),
      {
        key: 'manufacturer',
        label: trc('manufacturer', 'Fabricante / Modelo'),
        render: (item) => ([item.manufacturer, item.model].filter(Boolean).join(' ') || dash),
      },
      {
        key: 'status',
        label: tr('list.status', 'Estado'),
        sortable: true,
        sortValue: (item) => item.status || '',
        render: (item) => {
          const meta = STATUS_META[item.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'pdu_phase',
        label: trc('phase', 'Fase'),
        sortable: true,
        render: (item) => item.pdu_phase || dash,
      },
      {
        key: 'pdu_amperage',
        label: trc('amperage', 'Amperaje'),
        sortable: true,
        sortValue: (item) => item.pdu_amperage || 0,
        render: (item) => (item.pdu_amperage ? `${item.pdu_amperage} A` : dash),
      },
      {
        key: 'serial_number',
        label: tr('list.serial', 'Nº de serie'),
        sortable: true,
        render: (item) => item.serial_number || dash,
      },
      {
        key: 'ip_address',
        label: 'IP',
        sortable: true,
        render: (item) => (item.ip_address ? <span className="list-code">{item.ip_address}</span> : dash),
      },
      {
        key: 'notes',
        label: tr('list.notes', 'Notas'),
        render: (item) => item.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, trc, cfg.i18n]);

  const defaultVisible = cfg.i18n === 'switch'
    ? ['name', 'code', 'ports', 'occupancy', 'location_name', 'controller_name', 'status']
    : ['name', 'code', 'ports', 'occupancy', 'location_name', 'rack_name', 'status'];

  const exportRows = useMemo(() => filtered.map((item) => {
    const summary = summaryOf(item);
    const total = summary.total || 0;
    return {
      'Nombre': item.name,
      'Código': item.code || '',
      'Tipo': cfg.singular,
      'Puertos': total || '',
      'Conectados': summary.connected || 0,
      'Ocupación (%)': total > 0 ? occupancyPct(item) : '',
      'Ubicación': item.location_name || '',
      'Rack': item.rack_name || '',
      'Posición (U)': item.position_u ?? '',
      'Fabricante': item.manufacturer || '',
      'Modelo': item.model || '',
      'Nº de serie': item.serial_number || '',
      'IP': item.ip_address || '',
      ...(cfg.i18n === 'switch' ? { 'Controlador': item.controller_name || '' } : {}),
      'PoE': item.poe_capable ? (item.poe_budget_w ? `Sí (${item.poe_budget_w} W)` : 'Sí') : 'No',
      'Fase': item.pdu_phase || '',
      'Amperaje (A)': item.pdu_amperage ?? '',
      'Estado': (STATUS_META[item.status] || STATUS_META.active).label,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filtered, cfg.singular]);

  const portWord = cfg.i18n === 'pdu' ? 'enchufes' : 'puertos';
  const subtitle = `${items.length} ${cfg.singular.toLowerCase()}`
    + (totalPorts > 0 ? ` · ${totalConnected}/${totalPorts} ${portWord} ocupados` : '')
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  /** Tarjetas: lo esencial de cada elemento, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((item) => {
          const s = summaryOf(item);
          const meta = STATUS_META[item.status] || STATUS_META.active;
          const pct = occupancyPct(item);
          return (
            <div
              key={item.id}
              className="list-card"
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(item)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(item); }}
              title={`${tr('action.view', 'Ver')} ${item.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{cfg.icon} {item.name}</div>
                  {item.code && <span className="list-code">{item.code}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              {s.total > 0 && (
                <div>
                  <span className="list-hint">
                    {s.connected}/{s.total} {portWord} · {pct}%
                  </span>
                  <span className="pel-port-bar" style={{ display: 'block', width: '100%', marginTop: 4 }}>
                    <span className="pel-port-bar-fill" style={{ width: `${pct}%` }} />
                  </span>
                </div>
              )}

              <div className="list-card-meta">
                {item.poe_capable && (
                  <span className="list-badge list-badge--warn">
                    ⚡ {item.poe_budget_w ? `${item.poe_budget_w} W` : 'PoE'}
                  </span>
                )}
                {(item.manufacturer || item.model) && (
                  <span>🔧 {[item.manufacturer, item.model].filter(Boolean).join(' ')}</span>
                )}
                {(item.location_name || item.rack_name) && (
                  <span>📍 {[item.location_name, item.rack_name].filter(Boolean).join(' › ')}</span>
                )}
                {item.position_u != null && <span>U{item.position_u}</span>}
              </div>

              <div className="list-card-actions">{rowActions(item)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const VIEWS = useMemo(() => [
    { key: 'list', label: tr('list.view_list', 'Lista'), icon: ListBulletIcon, title: tr('list.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('list.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('list.view_cards_hint', 'Tarjetas con lo esencial'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, cfg.i18n]);
  useListView(cfg.i18n, 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">Selecciona una empresa para ver {cfg.title}.</div>
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <PortElementForm
          elementType={elementType}
          item={editItem}
          companyId={companyId}
          initialMode={formMode}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
            // Al cerrar se recarga el listado: añadir puertos desde la ficha
            // cambia `port_count` en el backend y la fila seguiría con el viejo.
            fetchItems();
          }}
          onSave={(creado) => {
            fetchItems();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setEditItem(creado); setFormMode('edit'); }
          }}
          navItems={navItems}
          navIndex={editItem ? navItems.findIndex(i => i.id === editItem.id) : -1}
          onNavigate={(it) => setEditItem(it)}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={cfg.heroIcon}
          title={cfg.title}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={`＋ ${trc('new', `Nuevo ${cfg.singular}`)}`}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={trc('search_placeholder', `Buscar ${cfg.singular.toLowerCase()} por nombre, código, serie, IP, rack…`)}
          filters={filters}
          exportRows={exportRows}
          exportFileBase={cfg.exportBase}
          exportSheetName={cfg.title}

          columns={columns}
          columnStorageKey={`cmdb_cols_${cfg.i18n}`}
          companyId={companyId}
          importEntity={elementType === 'switch' ? 'switches' : undefined}
          onImported={() => fetchItems()}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(item) => item.id}
          rowId={(item) => `port-element-row-${item.id}`}
          rowClassName={(item) => (item.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={handleOpen}
          rowTitle={(item) => `${tr('action.view', 'Ver')} ${item.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="elements"
                supportsRack
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchItems();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="net_devices"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchItems();
                }}
              />
            </>
          )}

          views={VIEWS}
          listKey={cfg.i18n}
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando…')}
          error={error}

          empty={{
            icon: cfg.icon,
            title: items.length === 0
              ? trc('empty', cfg.emptyMsg)
              : tr('msg.no_results', 'Ningún elemento coincide con los filtros'),
            subtitle: items.length === 0 ? trc('empty_hint', 'Crea el primero para empezar.') : undefined,
            action: items.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {trc('new', `Nuevo ${cfg.singular}`)}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (item) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => handleOpenPortModal(item)}
                  title={trc('view_ports', cfg.viewPorts)}
                  disabled={!summaryOf(item).total}
                >
                  <MapIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!item.rack_id}
                  onClick={() => item.rack_id && navigate(`/infrastructure/rack/${item.rack_id}`)}
                  title={tr('list.view_rack', 'Ver rack')}
                >
                  <CubeIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(cfg.wiringPath)}
                  title={trc('wiring', cfg.wiringLabel)}
                >
                  {cfg.wiringIcon === 'bolt' ? <BoltIcon /> : <ShareIcon />}
                </button>
                {cfg.hasComponents && (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => setComponentsItem(item)}
                    title={tr('list.components', 'Componentes')}
                  >
                    <CpuChipIcon />
                  </button>
                )}
              </>
            ),
            onCopy: (item) => { setDialogError(''); setItemToCopy(item); },
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: trc('delete_title', `Eliminar ${cfg.singular}`),
            deleteMessage: (item) => (
              <>
                {trc('delete_confirm', `¿Seguro que quieres eliminar el ${cfg.singular.toLowerCase()}`)}{' '}
                <strong>{item.name}</strong>?{' '}
                {cfg.i18n === 'pdu'
                  ? trc('delete_hint', 'Se eliminarán también sus enchufes y las conexiones de alimentación que cuelgan de ellos. No se puede deshacer.')
                  : trc('delete_hint', 'Se eliminarán también sus puertos y las conexiones que cuelgan de ellos. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: copiar elemento */}
      {itemToCopy && (
        <CloneDialog
          title={trc('copy_title', `Copiar ${cfg.singular}`)}
          nameLabel={trc('copy_name', 'Nombre de la copia')}
          placeholder={trc('copy_placeholder', 'Nombre del nuevo elemento…')}
          defaultName={`${itemToCopy.name} (copia)`}
          hint={cfg.i18n === 'pdu'
            ? trc('copy_hint', 'Se copian fabricante, modelo, altura, fase, amperaje y nº de enchufes. No se copian el número de serie, la IP ni la regleta a la que está conectada; la PDU nueva nace sin enchufes ocupados.')
            : trc('copy_hint', 'Se copian fabricante, modelo, altura y los puertos (sin sus conexiones). No se copian el número de serie ni la IP, y el elemento nuevo nace fuera del rack.')}
          busy={copyBusy}
          error={dialogError}
          onClose={() => { setItemToCopy(null); setDialogError(''); }}
          onConfirm={handleCopy}
        />
      )}

      {/* Diálogo: componentes (NICs / HBAs) */}
      {componentsItem && (
        <ComponentsModal
          element={componentsItem}
          companyId={companyId}
          onClose={() => setComponentsItem(null)}
        />
      )}

      {/* Mapa de puertos (paneles y switches) */}
      {portModalItem && elementType !== 'pdu' && (
        <PortMapModal
          element={portModalItem}
          rackElements={portModalRackElements}
          companyId={companyId}
          onClose={() => setPortModalItem(null)}
          onUpdated={fetchItems}
        />
      )}

      {/* Mapa de enchufes (PDUs) */}
      {portModalItem && elementType === 'pdu' && (
        <Dialog
          title={`⚡ ${portModalItem.name}`}
          maxWidth={860}
          onClose={() => setPortModalItem(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setPortModalItem(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            {portModalItem.location_name || ''}{portModalItem.rack_name ? ` · ${portModalItem.rack_name}` : ''} · Enchufes PDU
          </p>
          <PDUOutletMap pduId={portModalItem.id} companyId={companyId} />
        </Dialog>
      )}
    </>
  );
}

export default PortElementsList;
